// =============================================================================
// V2QuestionButtons.tsx — V2 inline question buttons with answer options
//
// Both single-question and multi-question modes collect selections in local
// state and show a "Submit Answers" button. Submission only happens when the
// user presses Submit — no auto-send on tap.
//
// Mutual exclusivity: selecting a text answer clears "upload_now" for that
// question, and selecting "upload_now" clears any text answer selection.
// =============================================================================

import React, { useCallback, useState } from 'react';
import { Loader2, RotateCcw, X, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import type {
  QuestionObject,
  AttachmentItem,
} from '../../../application/services/notesToHank/NotesToHankTypes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface V2QuestionButtonsProps {
  questions: QuestionObject[];
  onAnswerSelect: (
    questionIndex: number,
    answerIndex: number,
    answerId: string,
    answerText: string,
    questionId?: string
  ) => void;
  onBatchSubmit?: (
    answerIds: string[],
    combinedText: string,
    selectedAnswersMap: Record<string, string>
  ) => void;
  selectedAnswers: Record<string, string>;
  questionAttachments?: Record<string, AttachmentItem[]>;
  onRemoveQuestionAttachment?: (questionId: string, index: number) => void;
  onRetryUpload?: (questionId: string, attachmentIndex: number) => void;
  isUploading?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function V2QuestionButtons({
  questions,
  onAnswerSelect,
  onBatchSubmit,
  selectedAnswers,
  questionAttachments = {},
  onRemoveQuestionAttachment,
  onRetryUpload,
  isUploading = false,
}: V2QuestionButtonsProps) {
  // Local state for batching selections before submit.
  const [pendingSelections, setPendingSelections] = useState<
    Record<string, { answerId: string; answerText: string }>
  >({});

  // Track whether this batch has already been submitted.
  const [submitted] = useState(() => Object.keys(selectedAnswers).length > 0);

  // Check if any attachment is currently uploading
  const hasUploadingAttachment = Object.values(questionAttachments).some(
    (atts) => atts.some((att) => att.uploadStatus === 'uploading')
  );

  // A question is "answered" if it has a pending selection, a previous answer,
  // or attached photos
  const isQuestionAnswered = useCallback(
    (questionId: string) => {
      if (pendingSelections[questionId]) return true;
      if (selectedAnswers[questionId]) return true;
      const atts = questionAttachments[questionId];
      if (atts && atts.length > 0) return true;
      return false;
    },
    [pendingSelections, selectedAnswers, questionAttachments]
  );

  const allAnswered = questions.every((q) => isQuestionAnswered(q.id));

  const handleAnswerTap = useCallback(
    (
      questionId: string,
      questionIndex: number,
      answerIndex: number,
      answerId: string,
      answerText: string
    ) => {
      if (submitted) return;

      if (answerId === 'upload_now') {
        // Clear any text selection for this question (mutual exclusivity)
        setPendingSelections((prev) => {
          const { [questionId]: _, ...rest } = prev;
          return rest;
        });
        onAnswerSelect(
          questionIndex,
          answerIndex,
          answerId,
          answerText,
          questionId
        );
        return;
      }

      // Regular text answer — store selection (clears upload_now implicitly
      // since we're replacing the pending selection for this question)
      setPendingSelections((prev) => ({
        ...prev,
        [questionId]: { answerId, answerText },
      }));
    },
    [onAnswerSelect, submitted]
  );

  const handleBatchSubmit = useCallback(() => {
    if (!allAnswered || !onBatchSubmit) return;

    const answerIds: string[] = [];
    const lines: string[] = [];
    const selectedAnswersMap: Record<string, string> = {};

    for (const q of questions) {
      const selection = pendingSelections[q.id];
      const atts = questionAttachments[q.id];
      const hasAttachments = atts && atts.length > 0;

      if (selection) {
        answerIds.push(selection.answerId);
        lines.push(`${q.question}: ${selection.answerText}`);
        selectedAnswersMap[q.id] = selection.answerId;
      } else if (hasAttachments) {
        answerIds.push('upload_now');
        lines.push(`${q.question}: Attached ${atts.length} photo(s)`);
        selectedAnswersMap[q.id] = 'upload_now';
      }
    }

    const combinedText = lines.join('\n');
    onBatchSubmit(answerIds, combinedText, selectedAnswersMap);
  }, [
    allAnswered,
    pendingSelections,
    questionAttachments,
    questions,
    onBatchSubmit,
  ]);

  return (
    <div className="mt-2">
      {questions.map((q, qIdx) => {
        const selection = pendingSelections[q.id];
        const previousAnswer = selectedAnswers[q.id];
        const attachments = questionAttachments[q.id] || [];
        const hasAttachments = attachments.length > 0;
        const selectedId =
          selection?.answerId ||
          previousAnswer ||
          (hasAttachments ? 'upload_now' : undefined);

        return (
          <div key={q.id} className="mb-3">
            <p className="text-sm text-foreground mb-1.5">{q.question}</p>

            {q.answers.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {q.answers.map((answer, aIdx) => {
                  const isUploadButton = answer.id === 'upload_now';
                  const isSelected = isUploadButton
                    ? selectedId === 'upload_now'
                    : selectedId === answer.id;
                  const isDisabled = submitted;

                  // Highlight upload button with primary variant when selected
                  const variant = isSelected ? 'default' : 'outline';

                  return (
                    <Button
                      key={answer.id}
                      variant={variant}
                      size="sm"
                      disabled={isDisabled}
                      className={`rounded-full ${
                        isDisabled && isSelected
                          ? 'opacity-70 bg-primary text-primary-foreground'
                          : ''
                      }`}
                      onClick={() =>
                        handleAnswerTap(
                          q.id,
                          qIdx,
                          aIdx,
                          answer.id,
                          answer.text
                        )
                      }
                      aria-label={`Answer: ${answer.text}${
                        isSelected ? ', selected' : ''
                      }${
                        isUploadButton && hasAttachments
                          ? ` (${attachments.length} attached)`
                          : ''
                      }`}
                    >
                      {isUploadButton && <Upload className="size-3.5" />}
                      {isUploadButton && hasAttachments
                        ? `${answer.text} (${attachments.length})`
                        : answer.text}
                    </Button>
                  );
                })}
              </div>
            )}

            {/* Per-question attachment thumbnails */}
            {hasAttachments && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {attachments.map((att, attIdx) => (
                  <div
                    key={`${q.id}-att-${attIdx}`}
                    className={`relative w-14 h-14 rounded-lg overflow-hidden border ${
                      att.uploadStatus === 'error'
                        ? 'border-destructive opacity-70'
                        : 'border-border'
                    }`}
                  >
                    {att.uri ? (
                      <img
                        src={att.uri}
                        alt={att.fileName || `Photo ${attIdx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted" />
                    )}

                    {/* Spinner overlay for uploading */}
                    {att.uploadStatus === 'uploading' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <Loader2 className="size-5 text-white animate-spin" />
                      </div>
                    )}

                    {/* Error/retry overlay */}
                    {att.uploadStatus === 'error' && (
                      <div
                        className={`absolute inset-0 flex flex-col items-center justify-center bg-black/50 gap-0.5 ${
                          onRetryUpload ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => onRetryUpload?.(q.id, attIdx)}
                        role="button"
                        aria-label={`Retry upload for photo ${attIdx + 1}`}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            onRetryUpload?.(q.id, attIdx);
                          }
                        }}
                      >
                        <RotateCcw className="size-4 text-white" />
                        <span className="text-white text-[8px] leading-none">
                          Retry
                        </span>
                      </div>
                    )}

                    {/* Remove button — only when not submitted */}
                    {!submitted && onRemoveQuestionAttachment && (
                      <button
                        onClick={() => onRemoveQuestionAttachment(q.id, attIdx)}
                        aria-label={`Remove photo ${attIdx + 1}`}
                        className="absolute -top-1 -right-1 w-[18px] h-[18px] rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
                      >
                        <X className="size-3 text-white" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Submit button — shown for both single and multi-question groups */}
      {!submitted && (
        <div className="mt-3">
          <Button
            variant="default"
            size="sm"
            disabled={!allAnswered || isUploading || hasUploadingAttachment}
            className="rounded-full"
            onClick={handleBatchSubmit}
            aria-label="Submit all answers"
          >
            Submit Answers
          </Button>
        </div>
      )}
    </div>
  );
}

export default V2QuestionButtons;
